/**
 * Roles sagas
 */
import { call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import { httpRequest } from '../../services/http-service';
import { endPoints } from '../../services/endpoints';
import {
  fetchRoles,
  fetchRole,
  createRole,
  updateRoleAction,
  deleteRole,
  assignRoleToUser,
  removeRoleFromUser,
  getUsersByRole,
  setRoles,
  setCurrentRole,
  addRole,
  updateRole,
  removeRole,
  setLoading,
  setError,
} from './rolesSlice';
import { presentLoader, dismissLoader } from '../../store/slices/commonSlice';

/**
 * Fetch all roles saga
 */
function* fetchRolesSaga(): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.roles.getAll);

    if (response?.success) {
      yield put(setRoles(response.data || response));
    } else {
      toast.error('Failed to fetch roles');
      yield put(setError('Failed to fetch roles'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch roles';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Fetch single role saga
 */
function* fetchRoleSaga(action: ReturnType<typeof fetchRole>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.roles.getById(action.payload));

    if (response?.success) {
      yield put(setCurrentRole(response.data || response));
    } else {
      toast.error('Failed to fetch role');
      yield put(setError('Failed to fetch role'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch role';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Create role saga
 */
function* createRoleSaga(action: ReturnType<typeof createRole>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.post, endPoints.roles.create, action.payload);

    if (response?.success) {
      const newRole = response.data || response;
      yield put(addRole(newRole));
      toast.success('Role created successfully');
    } else {
      toast.error('Failed to create role');
      yield put(setError('Failed to create role'));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message || error?.message || 'Failed to create role';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Update role saga
 */
function* updateRoleSaga(action: ReturnType<typeof updateRoleAction>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.put,
      endPoints.roles.update(action.payload.id),
      action.payload.data
    );

    if (response?.success) {
      const updatedRole = { ...response.data, _id: action.payload.id };
      yield put(updateRole(updatedRole));
      toast.success('Role updated successfully');
    } else {
      toast.error('Failed to update role');
      yield put(setError('Failed to update role'));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message || error?.message || 'Failed to update role';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Delete role saga
 */
function* deleteRoleSaga(action: ReturnType<typeof deleteRole>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.delete, endPoints.roles.delete(action.payload));

    if (response?.success || response) {
      yield put(removeRole(action.payload));
      toast.success('Role deleted successfully');
    } else {
      toast.error('Failed to delete role');
      yield put(setError('Failed to delete role'));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message || error?.message || 'Failed to delete role';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Assign role to user saga
 */
function* assignRoleToUserSaga(action: ReturnType<typeof assignRoleToUser>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(httpRequest.post, endPoints.roles.assign, {
      userId: action.payload.userId,
      roleId: action.payload.roleId,
    });

    if (response?.success) {
      toast.success('Role assigned successfully');
    } else {
      toast.error('Failed to assign role');
      yield put(setError('Failed to assign role'));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message || error?.message || 'Failed to assign role';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Remove role from user saga
 */
function* removeRoleFromUserSaga(action: ReturnType<typeof removeRoleFromUser>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(httpRequest.delete, endPoints.roles.remove(action.payload));

    if (response?.success || response) {
      toast.success('Role removed successfully');
    } else {
      toast.error('Failed to remove role');
      yield put(setError('Failed to remove role'));
    }
  } catch (error: any) {
    const message = error?.response?.data?.message || error?.message || 'Failed to remove role';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Get users by role saga
 */
function* getUsersByRoleSaga(action: ReturnType<typeof getUsersByRole>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.roles.getUsersByRole(action.payload));

    if (response?.success) {
      // Return users data - can be handled by component
      return response.data || response;
    } else {
      toast.error('Failed to fetch users by role');
      yield put(setError('Failed to fetch users by role'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch users by role';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

export function* rolesSagas(): Generator<any, void, any> {
  yield takeLatest(fetchRoles, fetchRolesSaga);
  yield takeLatest(fetchRole, fetchRoleSaga);
  yield takeLatest(createRole, createRoleSaga);
  yield takeLatest(updateRoleAction, updateRoleSaga);
  yield takeLatest(deleteRole, deleteRoleSaga);
  yield takeLatest(assignRoleToUser, assignRoleToUserSaga);
  yield takeLatest(removeRoleFromUser, removeRoleFromUserSaga);
  yield takeLatest(getUsersByRole, getUsersByRoleSaga);
}

